import { useMemo } from "react";
import AllInboxRoundedIcon from "@mui/icons-material/AllInboxRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import NoteAltRoundedIcon from "@mui/icons-material/NoteAltRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import { Box, Sheet, Typography } from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";

import { useTranslation } from "../../i18n";
import { purplePalette } from "../../theme/purplePalette";
import { isMac } from "../../utils/platform";

// Visual metadata for the Cmd+Tab–style service switcher overlay. Indexed
// by the same `id` used in `SERVICES_BY_ID` (hooks/common/useGlobalServiceShortcut.ts)
// and `NAV_ITEMS` (components/layout/sidebar.tsx). All three lists must stay
// in sync — order and ids must match.
const OVERLAY_SERVICES: Array<{
    labelKey: "inbox" | "chats" | "tasks" | "notes" | "search" | "todo";
    icon: typeof AllInboxRoundedIcon;
}> = [
    { labelKey: "inbox", icon: AllInboxRoundedIcon },
    { labelKey: "chats", icon: QuestionAnswerRoundedIcon },
    { labelKey: "tasks", icon: AssignmentRoundedIcon },
    { labelKey: "notes", icon: NoteAltRoundedIcon },
    // The Genos page. `search` is the sidebar key that carries the
    // "Genos" label (kept to avoid a cross-locale rename).
    { labelKey: "search", icon: AutoAwesomeRoundedIcon },
    // To-Do (MIP-1044): no page — a stop on it opens the to-do modal. Same
    // icon as the sidebar's To-Do button.
    { labelKey: "todo", icon: ChecklistRoundedIcon },
];

type ServiceSwitcherOverlayProps = {
    /**
     * Position (within `mruOrder`) of the highlighted service, or `null`
     * to hide the overlay. Comes straight from `useGlobalServiceShortcut`.
     */
    previewIndex: number | null;
    /**
     * Service ids in most-recently-used order. The overlay renders tiles
     * left-to-right in this order so the most-recent service is always
     * leftmost (matching macOS Cmd+Tab).
     */
    mruOrder: number[];
    /** The pointer really moved over the tile at this position. */
    onHighlight?: (positionInMru: number) => void;
    /** The tile at this position was clicked. */
    onSelect?: (positionInMru: number) => void;
};

/**
 * macOS Cmd+Tab–style overlay for the global service switcher. The
 * fullscreen layer has `pointer-events: none`, so it never intercepts clicks
 * on the page; only the sheet itself takes the pointer, so its tiles can be
 * hovered and clicked (MIP-1044). Visibility is driven entirely by
 * `previewIndex`.
 */
export const ServiceSwitcherOverlay = ({
    previewIndex,
    mruOrder,
    onHighlight,
    onSelect,
}: ServiceSwitcherOverlayProps) => {
    const { mode } = useColorScheme();
    const { t } = useTranslation();
    const isDark = mode === "dark";
    const palette = isDark ? purplePalette.dark : purplePalette.light;

    // Hint matches the cycle gesture in `useGlobalServiceShortcut`: hold the
    // platform "switcher" modifier (Cmd on mac, Alt elsewhere) and tap Ctrl
    // to advance the highlight.
    const shortcutHint = useMemo(() => {
        return isMac() ? t.layout.serviceSwitcher.hintMac : t.layout.serviceSwitcher.hintOther;
    }, [t.layout.serviceSwitcher.hintMac, t.layout.serviceSwitcher.hintOther]);

    if (previewIndex === null) return null;

    const accent = isDark ? purplePalette.dark.accentSoft : purplePalette.light.accent;
    const accentRgb = isDark ? purplePalette.dark.accentSoftRgb : purplePalette.light.accentRgb;

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 13000,
                pointerEvents: "none",
            }}
            aria-hidden
        >
            <Sheet
                variant="soft"
                sx={{
                    pointerEvents: "auto",
                    px: 3,
                    py: 2.5,
                    borderRadius: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                    backdropFilter: "blur(20px) saturate(180%)",
                    WebkitBackdropFilter: "blur(20px) saturate(180%)",
                    background: isDark
                        ? "rgba(var(--gp-dark-surface-a-rgb), 0.82)"
                        : "rgba(250,248,255,0.85)",
                    border: "1px solid",
                    borderColor: palette.border,
                    boxShadow: isDark
                        ? "0 24px 60px rgba(0,0,0,0.55), 0 0 40px rgba(var(--gp-brand-700-rgb), 0.18)"
                        : "0 24px 60px rgba(var(--gp-brand-700-rgb), 0.18)",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    {mruOrder.map((serviceId, position) => {
                        const service = OVERLAY_SERVICES[serviceId];
                        if (!service) return null;
                        const Icon = service.icon;
                        const isActive = position === previewIndex;
                        return (
                            <Box
                                key={serviceId}
                                data-service-tile
                                onClick={() => onSelect?.(position)}
                                // `mousemove`, not `mouseenter`: the overlay appears under a
                                // pointer that is not moving, and the browser then reports
                                // that pointer as entering a tile, which must not steal the
                                // highlight from the tap that raised it. Only real movement
                                // counts.
                                onMouseMove={(e) => {
                                    if ((e.movementX !== 0 || e.movementY !== 0) && !isActive) {
                                        onHighlight?.(position);
                                    }
                                }}
                                sx={{
                                    cursor: "pointer",
                                    width: 88,
                                    height: 96,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0.75,
                                    borderRadius: "16px",
                                    px: 1,
                                    transition: "background 0.15s ease, border-color 0.15s ease",
                                    border: "1.5px solid",
                                    borderColor: isActive
                                        ? isDark
                                            ? `rgba(${accentRgb}, 0.502)`
                                            : `rgba(${accentRgb}, 0.439)`
                                        : "transparent",
                                    background: isActive
                                        ? isDark
                                            ? `linear-gradient(135deg, rgba(${accentRgb}, 0.188) 0%, rgba(${accentRgb}, 0.094) 100%)`
                                            : `linear-gradient(135deg, rgba(${accentRgb}, 0.133) 0%, rgba(${accentRgb}, 0.071) 100%)`
                                        : "transparent",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: "14px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: isActive
                                            ? isDark
                                                ? `linear-gradient(135deg, rgba(${accentRgb}, 0.208) 0%, rgba(${accentRgb}, 0.125) 100%)`
                                                : `linear-gradient(135deg, rgba(${accentRgb}, 0.157) 0%, rgba(${accentRgb}, 0.082) 100%)`
                                            : isDark
                                              ? "rgba(255,255,255,0.06)"
                                              : "rgba(0,0,0,0.04)",
                                    }}
                                >
                                    <Icon
                                        sx={{
                                            fontSize: 28,
                                            color: isActive
                                                ? accent
                                                : isDark
                                                  ? "rgba(255,255,255,0.55)"
                                                  : "rgba(0,0,0,0.5)",
                                        }}
                                    />
                                </Box>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontWeight: isActive ? 700 : 500,
                                        color: isActive
                                            ? accent
                                            : isDark
                                              ? "rgba(255,255,255,0.65)"
                                              : "rgba(0,0,0,0.6)",
                                    }}
                                >
                                    {t.layout.serviceSwitcher[service.labelKey]}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
                <Typography
                    level="body-xs"
                    sx={{
                        fontFamily: "monospace",
                        opacity: 0.65,
                        color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                    }}
                >
                    {shortcutHint}
                </Typography>
            </Sheet>
        </Box>
    );
};
